import React, { useCallback, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { StepsForm } from '~/components';

import { type RouteProp, useRoute } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import useAppNavigation from '~/hooks/useAppNavigation';
import {
  Document,
  InsuranceExpiry,
  PersonalInformation,
  PhoneNumber,
  Plate,
  QuizStorage,
  QuizUsage,
  Services,
  Vehicle,
  ZipCode,
} from './components';
import type { StepComponentProps, StepContent } from '~/components/StepsForm';
import LoadingSimulation from '~/scenes/MarisaAuto/Contract/components/LoadingSimulation';
import QuoteResult from '~/scenes/MarisaAuto/Contract/components/QuoteResult';
import useAlertReload from '~/hooks/useAlertReload';
import QuoteDetails from './components/QuoteDetails';
import QuoteResultEmpty from './components/QuoteResultEmpty';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import { useSalesFunnel } from '~/stores';
import { getPaymentMethods } from '~/utils/insuranceAuto';
import { AutoStepNames } from '~/utils/salesFunnel';

export interface MarisaAutoStepsProps extends StepComponentProps {
  onContinue: () => void;
  goToStep?: (step?: number) => void;
}

const MarisaAutoContract: React.FC = () => {
  const { navigate, push, replace } = useAppNavigation();

  const selectedInsuranceQuote = useMarisaAutoForm(
    (state) => state.selectedInsuranceQuote
  );
  const { newStepLog } = useSalesFunnel();

  const { params } =
    useRoute<
      RouteProp<RootStackParamList, 'MarisaServicos_MarisaAutoContract'>
    >();

  const currentStep = useMemo(() => {
    return Number(params?.step || 1);
  }, [params?.step]);

  useEffect(() => {
    if (!useMarisaAutoForm.getState().form?.vehicle.brand && currentStep > 2) {
      navigate('MarisaServicos_MarisaAutoContract', {});
    }
  }, [currentStep, navigate]);

  const goToStep = useCallback(
    (step: number, stepParams?: Object, replaceScreen?: boolean) => {
      if (replaceScreen) {
        replace('MarisaServicos_MarisaAutoContract', {
          step,
          ...stepParams,
        });
      } else {
        push('MarisaServicos_MarisaAutoContract', {
          step,
          ...stepParams,
        });
      }
    },
    [push, replace]
  );

  useAlertReload();

  const onContinue = useCallback(
    (replaceScreen?: boolean) => {
      goToStep(currentStep + 1, undefined, replaceScreen);
    },
    [currentStep, goToStep]
  );

  const onFinish = useCallback(() => {
    navigate('MarisaServicos_Checkout', {
      type: 'marisa-auto',
      paymentMethods: getPaymentMethods(selectedInsuranceQuote),
    });
  }, [navigate, selectedInsuranceQuote]);

  const stepsFormData = useMemo<StepContent[]>(() => {
    return [
      {
        step: 1,
        content: <Plate onContinue={onContinue} />,
        stepName: AutoStepNames.PLATE,
      },
      {
        step: 2,
        content: (
          <Vehicle onContinue={onContinue} goToStep={() => goToStep(4)} />
        ),
        stepName: AutoStepNames.VEHICLE,
      },
      {
        step: 3,
        content: <InsuranceExpiry onContinue={onContinue} />,
        stepName: AutoStepNames.INSURANCE_EXPIRY,
      },
      {
        step: 4,
        content: <QuizUsage onContinue={onContinue} />,
        stepName: AutoStepNames.QUIZ_USAGE,
      },
      {
        step: 5,
        content: (
          <QuizStorage
            step={5}
            questionId={params?.question}
            onContinue={onContinue}
          />
        ),
        stepName: `${AutoStepNames.QUIZ_STORAGE}-${params?.question ?? 1}`,
      },
      {
        step: 6,
        content: <ZipCode onContinue={onContinue} />,
        stepName: AutoStepNames.ADDRESS,
      },
      {
        step: 7,
        content: <Document onContinue={onContinue} />,
        stepName: AutoStepNames.DOCUMENT,
      },
      {
        step: 8,
        content: <Services onContinue={onContinue} />,
        stepName: AutoStepNames.SERVICES,
      },
      {
        step: 9,
        content: <PersonalInformation onContinue={onContinue} />,
        stepName: AutoStepNames.PERSONAL_INFO,
      },
      {
        step: 10,
        content: <PhoneNumber onContinue={onContinue} />,
        stepName: AutoStepNames.PHONE,
      },
      {
        step: 11,
        content: (
          <LoadingSimulation
            onContinue={() => onContinue(true)}
            goToStep={() => goToStep(14)}
          />
        ),
        stepName: AutoStepNames.LOADING,
      },
      {
        step: 12,
        content: <QuoteResult onContinue={onContinue} />,
        stepName: AutoStepNames.QUOTE_RESULT,
      },
      {
        step: 13,
        content: <QuoteDetails onContinue={onFinish} />,
        stepName: AutoStepNames.QUOTE_DETAILS,
      },
      {
        step: 14,
        content: <QuoteResultEmpty />,
        stepName: AutoStepNames.QUOTE_EMPTY,
      },
    ];
  }, [goToStep, onContinue, onFinish, params?.question]);

  const currentStepFormData = useMemo(
    () => stepsFormData.find((data) => data.step === currentStep),
    [currentStep, stepsFormData]
  );

  useEffect(() => {
    if (currentStepFormData?.stepName) {
      newStepLog(currentStepFormData.stepName);
    }
  }, [currentStepFormData?.stepName, newStepLog]);

  return (
    <View className="flex-1 bg-light-100">
      <StepsForm data={stepsFormData} currentStep={currentStep} />
    </View>
  );
};

export default MarisaAutoContract;
